import React from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

import { sourceCategoryPalette } from '../../bundles/MarketingInsights/styles/chartTokens';
const TOP_N = 6;
const DIRECT_LABEL = '(direct)';

const buildSlices = (rows = []) => {
  const labeled = rows.map((r) => ({
    label: r.utm_source || DIRECT_LABEL,
    revenue: r.revenue || 0
  }));
  const sorted = labeled.sort((a, b) => b.revenue - a.revenue);
  const top = sorted.slice(0, TOP_N);
  const rest = sorted.slice(TOP_N);
  if (rest.length > 0) {
    const otherRevenue = rest.reduce((sum, r) => sum + r.revenue, 0);
    if (otherRevenue > 0) top.push({ label: 'Other', revenue: otherRevenue });
  }
  return top.filter((r) => r.revenue > 0);
};

const formatCurrency = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatCompact = (n) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Number(n).toFixed(0)}`;
};

const SourceMixDonut = ({ rows, totalRevenue, attributionWindow }) => {
  const slices = buildSlices(rows);
  const total = Number(totalRevenue) || slices.reduce((s, r) => s + r.revenue, 0);
  const windowLabel = attributionWindow ? `last ${attributionWindow} days` : 'since on-sale';
  const palette = sourceCategoryPalette();

  return (
    <div className="mi-card mi-source-mix-donut">
      <div className="mi-source-mix-donut__head">
        <h3>Revenue by source</h3>
        <p className="mi-source-mix-donut__subtitle">
          {formatCurrency(total)} total · {windowLabel}
        </p>
      </div>

      {slices.length === 0 ? (
        <p className="mi-source-mix-donut__empty">No paid orders in this window.</p>
      ) : (
        <>
          <div className="mi-source-mix-donut__chart-wrap">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="revenue"
                  nameKey="label"
                  innerRadius={62}
                  outerRadius={88}
                  paddingAngle={2}
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                >
                  {slices.map((entry, i) => (
                    <Cell key={entry.label} fill={palette[i % palette.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  separator=": "
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mi-source-mix-donut__center">
              <strong>{formatCompact(total)}</strong>
              <span>{windowLabel}</span>
            </div>
          </div>

          <ul className="mi-source-mix-donut__legend">
            {slices.map((s, i) => {
              const sharePct = total > 0 ? Math.round((s.revenue / total) * 100) : 0;
              return (
                <li key={s.label} className="mi-source-mix-donut__legend-row">
                  <span
                    className="mi-source-mix-donut__swatch"
                    style={{ background: palette[i % palette.length] }}
                    aria-hidden="true"
                  />
                  <span className="mi-source-mix-donut__name">{s.label}</span>
                  <span className="mi-source-mix-donut__pct">· {sharePct}%</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

SourceMixDonut.propTypes = {
  rows: PropTypes.array,
  totalRevenue: PropTypes.number,
  attributionWindow: PropTypes.number
};

export default SourceMixDonut;
